
import { Vue, Component, Watch } from 'vue-property-decorator';
import { TableHeaderConfig } from '@/views/interfaces/vuetify/table.interface';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import RemoveServerDialog from '@/components/dialogs/server/RemoveServerDialog.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import StatusChip from '@/components/layout/StatusChip.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  inject: [
    'serverRepository',
  ],
  components: {
    RemoveServerDialog,
    PageHeader,
    StatusChip,
  },
})
export default class AllServers extends Vue {
  public readonly serverRepository!: ServerRepositoryInterface;

  public loading = true;
  public search = '';
  public options: ListPaginationOptions = defaultListOptions();
  public readonly LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public headers: TableHeaderConfig[] = [
    { text: 'Hostname', value: 'hostname' },
    { text: 'Type', value: 'type' },
    { text: 'Services', value: 'services' },
    { text: 'IPs', value: 'config.ips' },
    { text: 'Status', value: 'status' },
    { text: '', value: 'actions', sortable: false, width: '88px' },
  ];

  public servers: ServerInterface[] = [];

  public deleteServerDialogData: ServerInterface | false = false;
  public deleteServerDialog = '';

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.initialize();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const parsed = parseListQuery(this.$route.query);
      this.options.page = parsed.page;
      this.options.itemsPerPage = parsed.itemsPerPage;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({
        name: 'server_all',
        query: buildListPaginationQuery(this.options),
      });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  @Watch('options.page')
  @Watch('options.itemsPerPage')
  public async onPaginationChange(): Promise<void> {
    await this.syncRouteQuery();
  }

  @Watch('$route')
  public onRouteChange(): void {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'server_all') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }
    this.applyRouteQuery();
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;
      this.servers = await this.serverRepository.getAllServers();
    } catch (err) {
      this.$errorDialog.show(`Failed to retrieve servers. Error: ${(err as Error).message}`);
    } finally {
      this.loading = false;
    }
  }

  public async addServer(): Promise<void> {
    this.$router.push({ name: 'server_add' });
  }

  public async editServer(server: ServerInterface): Promise<void> {
    await this.$router.push({ name: 'server_edit', params: { server: server._id } });
  }

  public async removeServerPrompt(server: ServerInterface): Promise<void> {
    this.deleteServerDialog = 'remove-server-dialog';
    this.deleteServerDialogData = server;
  }

  public async removeServer(server: ServerInterface): Promise<void> {
    await this.serverRepository.removeServer(server._id);
    this.$successDialog.show(`Server ${server._id} has been removed.`);
    this.servers = await this.serverRepository.getAllServers();
  }
}
